import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import type { KeyboardEvent } from 'react';
import { type PropsWithChildren } from 'react';
import type { UseRefinementListProps } from 'react-instantsearch';
import { useRefinementList } from 'react-instantsearch';

import { Flex } from '@/components/Flex';
import { NextImage } from '@/components/NextImage';
import { FILTERS_FETCH_LIMIT } from '@/lib/const';
import { useRegionProvider } from '@/providers/RegionProvider';

import {
  CASE_COLOR_MAPPING,
  CASE_SVG_MAPPING,
  DIAL_COLOR_MAPPING,
  FRAME_COLOR_MAPPING,
  LENS_COLOR_MAPPING,
  STRAP_COLOR_MAPPING,
} from './colors';
import { getSwatchFilterItemValues } from './utils';

import GemSet from '@/assets/cases/gem-set.png';
import PlatinumCase from '@/assets/cases/platinum.svg';

import * as S from './SwatchFilter.styles';

type SwatchAttribute =
  | 'case'
  | 'dial_color'
  | 'color'
  | 'lens_color'
  | 'frame_color';

interface SwatchFilterWrapperProps extends Omit<
  UseRefinementListProps,
  'attribute'
> {
  attribute: SwatchAttribute;
  labelColorMapping?: Record<string, string>;
}

interface SwatchFilterProps extends SwatchFilterWrapperProps {
  items: RefinementListItem[];
  refine: (value: string) => void;
}

export const SwatchFilterWrapper = (props: SwatchFilterWrapperProps) => {
  const { locale: regionLocale } = useRegionProvider();
  const { items, refine } = useRefinementList({
    ...props,
    attribute: props.attribute
      ? `${props.attribute}.${regionLocale.code.toUpperCase()}`
      : '',
    limit: FILTERS_FETCH_LIMIT,
  });

  const filterRefine = (value: string) => {
    refine(value);
  };

  return (
    <SwatchFilter
      data-test-id='SwatchFilter'
      {...props}
      items={items}
      refine={filterRefine}
    />
  );
};

export const SwatchFilter = (props: PropsWithChildren<SwatchFilterProps>) => {
  const { attribute, items, refine } = props;
  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    value: string,
  ) => {
    if (event.key === ' ' || event.key === 'Enter') {
      refine(value);
    }
  };

  return (
    <Flex data-test-id='SwatchFilterFlex' flexWrap='wrap' gridGap={1}>
      <S.StyledGrid data-test-id='SwatchFilterStyledGrid'>
        {items.map(({ count, isRefined, value }, index) => {
          const { name } = getSwatchFilterItemValues(value);
          let { code } = getSwatchFilterItemValues(value);
          code = code ?? name;
          const isGemSet = attribute === 'case' && value?.includes('gem-set');

          return (
            <S.SwatchOption
              key={name || `swatch-${index}`}
              data-test-id={`SwatchFilterSwatchOption-${index}`}
              onClick={() => refine(value)}
              onKeyDown={(e) => handleKeyDown(e, value)}
              tabIndex={index + 1}
            >
              <S.IconWrapper>
                {isGemSet && code && !CASE_SVG_MAPPING[code] && (
                  <NextImage
                    alt='Gem-set'
                    height={24}
                    src={GemSet}
                    width={24}
                  />
                )}
                {attribute === 'case' &&
                  code &&
                  (CASE_SVG_MAPPING[code] ? (
                    <NextImage
                      alt={name || ''}
                      data-test-id='SwatchFilterNextImage'
                      height={24}
                      src={CASE_SVG_MAPPING[code] ?? PlatinumCase}
                      width={24}
                    />
                  ) : (
                    <S.SwatchIcon
                      $background={CASE_COLOR_MAPPING[code] ?? 'silver'}
                      $isGemSet={isGemSet}
                      $isRefined={isRefined}
                      data-test-id='SwatchFilterCaseColorSwatchIcon'
                    />
                  ))}

                {attribute === 'dial_color' && code && (
                  <S.SwatchIcon
                    $background={DIAL_COLOR_MAPPING[code] ?? 'silver'}
                    $isRefined={isRefined}
                    data-test-id='SwatchFilterDialColorSwatchIcon'
                  />
                )}
                {attribute === 'color' && code && (
                  <S.SwatchIcon
                    $background={STRAP_COLOR_MAPPING[code] ?? 'silver'}
                    $isRefined={isRefined}
                    data-test-id='SwatchFilterColorSwatchIcon'
                  />
                )}
                {attribute === 'lens_color' && (
                  <S.SwatchIcon
                    $background={
                      LENS_COLOR_MAPPING[code.toLocaleUpperCase()] ?? 'silver'
                    }
                    $isRefined={isRefined}
                    data-test-id='SwatchFilterLensColorSwatchIcon'
                  />
                )}
                {attribute === 'frame_color' && (
                  <S.SwatchIcon
                    $background={FRAME_COLOR_MAPPING[code] ?? 'silver'}
                    $isRefined={isRefined}
                    data-test-id='SwatchFilterFrameColorSwatchIcon'
                  />
                )}
              </S.IconWrapper>
              <S.Label data-test-id='SwatchFilterLabel' isRefined={isRefined}>
                {name} ({count})
              </S.Label>
            </S.SwatchOption>
          );
        })}
      </S.StyledGrid>
    </Flex>
  );
};

SwatchFilter.displayName = 'SwatchFilter';
